import { Info } from "lucide-react";
import React, { FC, ReactNode } from "react";

import type { Optional } from "@intelligems/ig-types/utils";
import {
  FormControl,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
  TypographyProps,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { IgTooltip } from "./IgTooltip";
import { IgTypography } from "./IgTypography";

export interface IgTextFieldProps extends Optional<TextFieldProps, "variant"> {
  label?: string | ReactNode | null;
  labelProps?: TypographyProps;
  tooltipMessage?: ReactNode;
  orientation?: "horizontal" | "vertical";
  tooltipMargin?: boolean;
  tooltipSx?: SxProps;
}

/**
 * @see TextField
 */
export const IgTextField: FC<IgTextFieldProps> = (props) => {
  const theme = useTheme();
  const generatedId = React.useId();
  const {
    children,
    label,
    labelProps,
    tooltipMessage,
    orientation = "vertical",
    tooltipMargin = true,
    required,
    id,
    sx,
    tooltipSx,
    ...rest
  } = props;
  const inputId = id ?? generatedId;

  if (!label) {
    return (
      <TextField id={id} required={required} sx={{ ...sx }} {...rest}>
        {children}
      </TextField>
    );
  }

  const renderLabelAndTooltip = (
    <Grid container alignItems="center" columnSpacing={1} wrap="nowrap">
      <Grid>
        <label htmlFor={inputId}>
          <IgTypography component="span" {...labelProps}>
            {label}
            {required && (
              <IgTypography
                {...labelProps}
                component="span"
                color={theme.palette.text.tonal.error}
              >
                {" "}
                *
              </IgTypography>
            )}
          </IgTypography>
        </label>
      </Grid>
      <Grid
        mt={tooltipMargin ? 1 : undefined}
        sx={{ minHeight: "20px", ...tooltipSx }}
      >
        {tooltipMessage && (
          <IgTooltip title={tooltipMessage}>
            <Info size={16} />
          </IgTooltip>
        )}
      </Grid>
    </Grid>
  );

  return (
    <FormControl sx={{ width: "100%" }}>
      {orientation === "vertical" ? (
        <Stack direction={"column"}>
          {renderLabelAndTooltip}
          <TextField
            id={inputId}
            required={required}
            sx={{ ...sx, marginTop: 1 }}
            {...rest}
          />
        </Stack>
      ) : (
        <Grid container alignItems="center" spacing={2}>
          <Grid sx={{ minWidth: "350px" }}>{renderLabelAndTooltip}</Grid>
          <Grid size="grow">
            <TextField
              id={inputId}
              required={required}
              sx={{ ...sx }}
              {...rest}
              fullWidth
            />
          </Grid>
        </Grid>
      )}
    </FormControl>
  );
};
